import * as SentryReact from '@sentry/react'

/**
 * Singleton for sentry
 *
 * uses static methods
 *
 */
export default class Sentry {
  private static instance: any

  constructor() {
    if (!Sentry.instance) {
      Sentry.instance = this.createInstance()
    }
    return Sentry.instance
  }

  private createInstance() {
    const dsn: string = SENTRY_DSN || ''
    const ENVIRONMENT: string = CURRENT_ENV || 'prod'
    const RELEASE = RELEASE_HASH || ''
    const isProd = ENVIRONMENT === 'prod'

    console.log('[Sentry] initializing...')
    try {
      SentryReact.init({
        dsn,
        // Setting this option to true will send default PII data to Sentry.
        // For example, automatic IP address collection on events
        sendDefaultPii: true,
        debug: !isProd,
        environment: ENVIRONMENT,
        release: RELEASE,
        integrations: [
          SentryReact.browserTracingIntegration(),
          ...(isProd
            ? [
                SentryReact.replayIntegration({
                  blockAllMedia: true,
                }),
              ]
            : []),
          SentryReact.captureConsoleIntegration({
            levels: ['error', 'assert', 'warn'],
          }),
          SentryReact.httpClientIntegration(),
        ],
        tracesSampleRate: 1.0, // 0.1, // isProd ? 0.01 : 0.1,
        replaysSessionSampleRate: 1.0, // 0.1,
        replaysOnErrorSampleRate: 1.0,
        enableLogs: true,
      })

      window.sentry = SentryReact

      console.log('[Sentry] initialized.')

      return window.sentry
    } catch (error) {
      console.warn('[Sentry] init failed!', error)

      delete window.sentry
      return null
    }
  }

  public static message(
    message: string,
    level?: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug',
  ): void {
    Sentry.instance?.withScope(function (scope: any) {
      if (level) scope.setLevel(level)
      scope.captureMessage(message)
    })
  }

  public static breadcrumb(breadcrumb: {
    type?: string
    category?: string
    message?: string
    level?: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug'
    timestamp?: string
    data?: any
  }): void {
    Sentry.instance?.addBreadcrumb(breadcrumb)
  }

  public static attach(
    filename: string,
    data: string | Uint8Array,
    contentType = 'application/octet-stream',
  ): void {
    Sentry.instance?.getCurrentScope().addAttachment({
      filename,
      data,
      contentType,
    })
  }

  public static clearAttachment(): void {
    Sentry.instance?.getCurrentScope().clearAttachments()
  }

  public static setUser(
    user: {
      id?: string
      username?: string
      email?: string
      ip_address?: string
    } & Record<string, string> = {},
  ): void {
    Sentry.instance?.setUser(user)
  }

  public static clearUser(): void {
    Sentry.instance?.setUser(null)
  }

  public static addTag(key: string, value: string): void {
    Sentry.instance?.setTag(key, value)
  }

  public static captureReactException(
    error: Error,
    context?: Record<string, any>,
  ): void {
    Sentry.instance?.captureException(error, context)
  }
}

export const SentryErrorBoundary = SentryReact.ErrorBoundary
export const withSentryProfiler = SentryReact.withProfiler

export const SentryRedux = SentryReact.createReduxEnhancer()
